import React, { useState, useEffect } from "react";
import "./JobSection.css";
import {
  PlayIcon,
  DocumentIcon,
  CircleStackIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  SparklesIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  FolderOpenIcon,
  CodeBracketIcon,
  DocumentMagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import JsonPreviewOverlay from '../JsonPreviewOverlay';
import {
  JobSectionProps,
  MetadataFile,
  DataFile,
  SelectOption,
  JobResult,
} from "../../types";
import ApiService from "../../services/api";
import { ColumnInfo } from "./ColumnTable/ColumnTable";
import Select from "react-select";
import DAPTable, { DAPTableColumn, DAPTableRow } from "../DAPTable";
import ColumnTable from "./ColumnTable/ColumnTable";
import ToastMessage from "../ToastMessage/ToastMessage";
import DataBox from "../DataBox";
import { useProject } from "../../contexts/ProjectContext";

const JobSection: React.FC<JobSectionProps> = ({ isActive }) => {
  const { selectedProject } = useProject();
  const [metadataFiles, setMetadataFiles] = useState<MetadataFile[]>([]);
  const [dataFiles, setDataFiles] = useState<DataFile[]>([]);
  const [selectedMetadata, setSelectedMetadata] = useState<SelectOption | null>(
    null
  );
  const [showModal, setShowModal] = useState(false);

  const [selectedData, setSelectedData] = useState<SelectOption | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isCreatingJob, setIsCreatingJob] = useState(false);
  const [jobResult, setJobResult] = useState<JobResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [metadataTableData, setMetadataTableData] = useState<DAPTableRow[]>([]);
  const [isGeneratingMetadata, setIsGeneratingMetadata] = useState(false);
  const [showMetadataTable, setShowMetadataTable] = useState(false);
  const [showDataFileReport, setShowDataFileReport] = useState(false);
  const [isDataFileReportCollapsed, setIsDataFileReportCollapsed] =
    useState(false);
  const [isMetadataTableCollapsed, setIsMetadataTableCollapsed] =
    useState(false);
  const [dataFileReportData, setDataFileReportData] = useState<DAPTableRow[]>(
    []
  );
  const [toastType, setToastType] = useState<"success" | "error" | "info">(
    "success"
  );
  const [metadataColumns, setMetadataColumns] = useState<DAPTableColumn[]>([]);
  const [metaColumnData, setMetaColumnData] = useState<ColumnInfo[]>([]);
  const [metaDataFileId, setMetaDataFileId] = useState<number>();
  const [showToast, setShowToast] = useState(false);
  const [preflightApiError, setPreflightApiError] = useState(false);
  const [selectedLocalFile, setSelectedLocalFile] = useState<File | null>(null);
  const [isUploadingFile, setIsUploadingFile] = useState(false);

  // Structural Relationship Definition state
  const [relationshipRows, setRelationshipRows] = useState<Array<{
    id: string;
    sourceColumn: SelectOption | null;
    targetColumn: SelectOption | null;
    relationshipFactor: SelectOption | null;
  }>>([{
    id: '1',
    sourceColumn: null,
    targetColumn: null,
    relationshipFactor: null
  }]);
  const [isMetadataValidationPassed, setIsMetadataValidationPassed] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingMetadata, setIsSavingMetadata] = useState(false);
  const [isSavingRelationships, setIsSavingRelationships] = useState(false);
  const [isSavingDescriptions, setIsSavingDescriptions] = useState(false);

  // Required Description Columns state
  const [selectedRoleFilter, setSelectedRoleFilter] = useState<SelectOption | null>(null);
  const [selectedColumns, setSelectedColumns] = useState<SelectOption[]>([]);
  const [availableColumnsForRole, setAvailableColumnsForRole] = useState<SelectOption[]>([]);
  
  // Metadata section navigation state
  const [currentMetadataSection, setCurrentMetadataSection] = useState<'metadata-info' | 'structural-relationships' | 'required-descriptions'>('metadata-info');

  // Add state variables for metadata file upload
  const [selectedLocalMetadataFile, setSelectedLocalMetadataFile] = useState<File | null>(null);
  const [isUploadingMetadataFile, setIsUploadingMetadataFile] = useState(false);
  const [uploadedMetadataData, setUploadedMetadataData] = useState<any>(null);

  // JSON Preview Overlay state
  const [showJsonOverlay, setShowJsonOverlay] = useState(false);
  const [jsonData, setJsonData] = useState<any>(null);
  const [jsonLoading, setJsonLoading] = useState(false);
  const [jsonError, setJsonError] = useState<string | null>(null);

  // Handler for Preview JSON button click
  const handlePreviewJson = async () => {
    if (!selectedMetadata) return;

    setJsonLoading(true);
    setJsonError(null);
    setShowJsonOverlay(true);

    try {
      const response = await ApiService.getProfilingRulesByMetadataId(selectedMetadata.value);
      setJsonData(response);
    } catch (error: any) {
      setJsonError(error.message || 'Failed to load profiling rules');
    } finally {
      setJsonLoading(false);
    }
  };

  // Handler for when metadata is saved from DAPTable
  const handleMetadataSaved = async (newMetadataFile: { id: number; name: string; path: string }) => {
    console.log('Metadata saved, refreshing dropdown and auto-selecting:', newMetadataFile);

    try {
      // Refresh the metadata files list
      const metadataResponse = await ApiService.getMetadataFiles();
      setMetadataFiles(metadataResponse.data);

      // Auto-select the newly saved metadata file
      const newMetadataOption: SelectOption = {
        value: newMetadataFile.id,
        label: newMetadataFile.name
      };

      setSelectedMetadata(newMetadataOption);
      console.log('Auto-selected new metadata file:', newMetadataOption);

    } catch (error) {
      console.error('Error refreshing metadata dropdown after save:', error);
    }
  };
  // Load files when component becomes active and project is known
  useEffect(() => {
    if (!isActive) return;
    if (!selectedProject?.id) return;
    loadFiles();
  }, [isActive, selectedProject?.id]);

  // When project changes, reset project-scoped state and refetch lists
  useEffect(() => {
    if (!isActive) return;
    if (!selectedProject?.id) return;
    // Reset selections and derived UI
    setSelectedMetadata(null);
    setSelectedData(null);
    setShowDataFileReport(false);
    setDataFileReportData([]);
    setPreflightApiError(false);
    setMetadataTableData([]);
    setMetadataColumns([]);
    // Refetch for new project
    loadFiles();
  }, [selectedProject?.id, isActive]);

  // Generate data file report when data file is selected
  useEffect(() => {
    if (selectedData) {
      generateDataFileReport();
    } else {
      setShowDataFileReport(false);
      setDataFileReportData([]);
      setPreflightApiError(false); // Clear error when no data file is selected
    }
  }, [selectedData]);

  // Load existing relationships and required description columns when metadata validation passes
  useEffect(() => {
    if (isMetadataValidationPassed && selectedMetadata && metadataTableData.length > 0) {
      loadExistingRelationships(selectedMetadata.value);
      loadExistingRequiredDescriptions(selectedMetadata.value);
    }
  }, [isMetadataValidationPassed, selectedMetadata, metadataTableData]);

  const loadFiles = async () => {
    setIsLoading(true);
    setError(null);

    try {
      const pid = selectedProject?.id;
      const [metadataResponse, dataResponse] = await Promise.all([
        ApiService.getMetadataFiles(0, 100, pid),
        ApiService.getDataFiles(0, 100, pid),
      ]);

      setMetadataFiles(metadataResponse.data);
      setDataFiles(dataResponse.data);
    } catch (err) {
      setError("Failed to load files. Please check if the API is running.");
      console.error("Error loading files:", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Helper functions to extract data from funputer 1.5.1 API response
  const extractFileStatus = (raw_report: any, formatted_report: any) => {
    // funputer 1.5.1 format: use status field
    if (raw_report.status) {
      return raw_report.status === "ok" ? "✅ OK" : "❌ HARD_ERROR";
    }
    return formatted_report?.status_label || "Unknown";
  };

  const extractFileSize = (raw_report: any) => {
    return raw_report.checks?.A1_path_size?.file_size || 0;
  };

  const extractFileFormat = (raw_report: any, formatted_report: any) => {
    const format = raw_report.checks?.A2_format?.format || "Unknown";
    const compression = raw_report.checks?.A2_format?.compression;
    return compression && compression !== "none" ? `${format} (${compression})` : format;
  };

  const extractNumColumns = (raw_report: any, formatted_report: any) => {
    return raw_report.checks?.A5_structure?.total_columns || "Unknown";
  };

  const extractFilePath = (raw_report: any, formatted_report: any) => {
    return raw_report.file_path || "Unknown";
  };

  const extractEncoding = (raw_report: any) => {
    return raw_report.checks?.A3_encoding?.encoding || "Unknown";
  };

  const extractColumnNames = (raw_report: any) => {
    return raw_report.checks?.A5_structure?.column_names || [];
  };

  const generateDataFileReport = async () => {
    if (!selectedData) return;

    try {
      // Call the preflight API to get real file analysis
      const preflightResponse = await ApiService.runPreflightCheck(
        selectedData.value
      );
      const { formatted_report, raw_report, file_id } = preflightResponse;
      setMetaDataFileId(file_id);

      // Transform the A5_structure data into the format expected by ColumnTable
      const columnData: ColumnInfo[] = [];
      if (raw_report?.checks?.A5_structure?.column_names) {
        const columnNames = raw_report.checks.A5_structure.column_names;
        const columnTypes = raw_report.checks.A5_structure.column_types || {};
        const missingCounts = raw_report.checks.A5_structure.missing_counts || {};
        const sampleRows = raw_report.checks.A5_structure.sample_rows || 1;

        columnNames.forEach((name: string) => {
          columnData.push({
            name,
            inferred_type: columnTypes[name] || 'unknown',
            missing_pct_sample: (missingCounts[name] || 0) / sampleRows
          });
        });
      }

      setMetaColumnData(columnData);
      setPreflightApiError(false); // Reset error state on successful API call

      // Transform the preflight response into comprehensive table data using helper functions
      const reportData: DAPTableRow[] = [
        // File Overview Section
        {
          property: "📄 File Status",
          value: extractFileStatus(raw_report, formatted_report),
          description: "Overall file validation status",
        },
        {
          property: "📁 File Name",
          value: selectedData.label,
          description: "Name of the selected data file",
        },
        {
          property: "📏 File Size",
          value: `${extractFileSize(raw_report)} bytes`,
          description: "Total size of the data file",
        },
        {
          property: "📋 File Format",
          value: extractFileFormat(raw_report, formatted_report),
          description: "Format and compression of the data file",
        },
        {
          property: "🗂️ Number of Columns",
          value: `${extractNumColumns(raw_report, formatted_report)}`,
          description: "Number of columns/fields in the dataset",
        },
        {
          property: "📍 File Path",
          value: extractFilePath(raw_report, formatted_report),
          description: "Location of the data file",
        },

        // Encoding Information
        {
          property: "🔤 Encoding",
          value: extractEncoding(raw_report),
          description: "Character encoding used to read the file",
        },

        // Memory Information
        {
          property: "💾 Memory Usage",
          value: `${raw_report.checks?.A6_memory?.estimated_memory_mb || 0} MB`,
          description: "Estimated memory required to process the file",
        },

        // Sample Information
        {
          property: "🔍 Sample Analysis",
          value: `${raw_report.checks?.A5_structure?.sample_rows || 0} rows sampled`,
          description: "Number of rows analyzed for inference",
        },

        // CSV Dialect (if applicable)
        ...(raw_report.checks?.A4_csv_dialect
          ? [
              {
                property: "📊 CSV Format",
                value: `Delimiter: "${
                  raw_report.checks.A4_csv_dialect.delimiter
                }", Quote: "${raw_report.checks.A4_csv_dialect.quotechar}", Header: ${
                  raw_report.checks.A4_csv_dialect.has_header ? "Yes" : "No"
                }`,
                description: "CSV parsing configuration detected",
              },
            ]
          : []),

        // Missing Data Summary
        {
          property: "📊 Missing Data",
          value: `${raw_report.checks?.A5_structure?.total_missing || 0} missing values total`,
          description: "Total count of missing values across all columns",
        },

        // Recommendation
        {
          property: "💡 Recommendation",
          value: raw_report.recommendation?.reason || "No specific recommendation",
          description: "System recommendation based on analysis",
        },

        // Next Steps (if available)
        ...(raw_report.recommendation?.next_steps?.length > 0
          ? [
              {
                property: "📋 Next Steps",
                value: raw_report.recommendation.next_steps.join("; "),
                description: "Recommended next actions",
              },
            ]
          : []),
      ];

      // Add column details section
      const columnNames = extractColumnNames(raw_report);
      if (columnNames?.length > 0) {
        reportData.push({
          property: "📋 Column Names",
          value: columnNames.join(", "),
          description: `All ${columnNames.length} column names in the dataset`,
        });
      }

      // Add detailed column analysis if available
      if (columnNames?.length > 0) {
        reportData.push({
          property: "🔍 Column Analysis",
          value: `${columnNames.length} columns detected`,
          description: "Column structure analysis performed",
        });
      }

      setDataFileReportData(reportData);
      setShowDataFileReport(true);
    } catch (error) {
      console.error("Failed to generate data file report:", error);
      setPreflightApiError(true);

      // Fallback to basic info if API fails
      const fallbackData: DAPTableRow[] = [
        {
          property: "📄 Status",
          value: "❌ Analysis Failed",
          description: "Could not analyze the file",
        },
        {
          property: "📁 File Name",
          value: selectedData.label,
          description: "Name of the selected data file",
        },
        {
          property: "❌ Error",
          value: "Failed to run preflight check",
          description: "Please check if the file exists and API is running",
        },
      ];

      setDataFileReportData(fallbackData);
      setShowDataFileReport(true);
    }
  };

  const handleCreateJob = async () => {
    if (!selectedMetadata || !selectedData) {
      setError("Please select both metadata and data files");
      return;
    }

    setIsCreatingJob(true);
    setError(null);
    setJobResult(null);

    try {
      const response = await ApiService.createJob({
        data_file_id: selectedData.value,
        metadata_file_id: selectedMetadata.value,
        started_by: "admin", // In a real app, this would come from user context
      });

      setJobResult({
        success: true,
        job_id: response.job_id,
        message: response.message,
      });
    } catch (err) {
      setError("Failed to create job. Please try again.");
      console.error("Error creating job:", err);
    } finally {
      setIsCreatingJob(false);
    }
  };

  const handleLocalFileSelection = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedLocalFile(file);
    setIsUploadingFile(true);
    setError(null);

    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);

      // Upload the file to the server
      const uploadResponse = await ApiService.uploadDataFile(formData);

      // Create a new option for the uploaded file
      const newFileOption: SelectOption = {
        value: uploadResponse.data_file_id,
        label: file.name,
      };

      // Add the new file to the data files list and select it
      setDataFiles(prev => [...prev, {
        id: uploadResponse.data_file_id,
        file_name: file.name,
        file_path: uploadResponse.file_path,
        created_at: new Date().toISOString(),
        created_by: uploadResponse.created_by,
        updated_at: new Date().toISOString(),
      }]);

      // Select the newly uploaded file
      setSelectedData(newFileOption);

      // Clear any previous errors
      setPreflightApiError(false);

    } catch (error) {
      console.error("Failed to upload file:", error);
      setError("Failed to upload file. Please try again.");
      setSelectedLocalFile(null);
    } finally {
      setIsUploadingFile(false);
      // Clear the file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  // Handler for local metadata file selection and upload
  const handleLocalMetadataFileSelection = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setSelectedLocalMetadataFile(file);
    setIsUploadingMetadataFile(true);
    setError(null);

    try {
      // Upload and view the metadata file using the inference API
      const response = await ApiService.uploadAndViewMetadataFile(file);

      console.log("Metadata file uploaded and viewed successfully:", response);

      // Set the uploaded metadata data to display in table
      setUploadedMetadataData(response);

      // Transform the response data into table format similar to handleMetadataInference
      if (response.full_metadata && response.full_metadata.length > 0) {
        const tableData: DAPTableRow[] = response.full_metadata.map(
          (field: any, index: number) => ({
            id: index + 1,
            ...field,
          })
        );

        // Generate dynamic columns based on the first row of data
        if (tableData.length > 0) {
          const sampleRow = tableData[0];
          const dynamicColumns: DAPTableColumn[] = Object.keys(sampleRow)
            .filter((key) => key !== "id")
            .map((key) => ({
              key,
              label: key.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()),
              width: key === "field_name" ? "20%" : "auto",
              render: (value: any) => {
                if (key === "nullable" || key === "unique") {
                  return (
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        value
                          ? "bg-green-100 text-green-800"
                          : "bg-red-100 text-red-800"
                      }`}
                    >
                      {value ? "Yes" : "No"}
                    </span>
                  );
                }
                if (key === "data_type") {
                  return <code className="text-sm">{value}</code>;
                }
                return value;
              },
            }));

          setMetadataColumns(dynamicColumns);
          setMetadataTableData(tableData);
          setShowMetadataTable(true);
          setIsMetadataTableCollapsed(false);
        }
      } else {
        setMetadataTableData([]);
        setMetadataColumns([]);
        setShowMetadataTable(false);
        setError("No metadata could be loaded from the selected file.");
      }

    } catch (error) {
      console.error("Metadata file upload failed:", error);
      setError("Failed to upload and process metadata file. Please try again.");
      setUploadedMetadataData(null);
      setMetadataTableData([]);
      setMetadataColumns([]);
      setShowMetadataTable(false);
    } finally {
      setIsUploadingMetadataFile(false);
      // Clear the file input
      if (event.target) {
        event.target.value = '';
      }
    }
  };

  const metadataOptions: SelectOption[] = metadataFiles.map((file) => ({
    value: file.id,
    label: file.file_name+'(ID:'+file.id+')',
  }));

  const dataOptions: SelectOption[] = dataFiles.map((file) => ({
    value: file.id,
    label: file.file_name+'(ID:'+file.id+')',
  }));

  const customSelectStyles = {
    control: (base: any, state: any) => ({
      ...base,
      minHeight: "48px",
      borderColor: state.isFocused ? "#3B82F6" : "#D1D5DB",
      boxShadow: state.isFocused ? "0 0 0 3px rgba(59, 130, 246, 0.1)" : "none",
      "&:hover": {
        borderColor: "#3B82F6",
      },
    }),
    option: (base: any, state: any) => ({
      ...base,
      backgroundColor: state.isSelected
        ? "#3B82F6"
        : state.isFocused
        ? "#EBF4FF"
        : "white",
      color: state.isSelected ? "white" : "#374151",
    }),
  };

  // Define columns for the data file report table
  const dataFileReportColumns: DAPTableColumn[] = [
    { key: "property", label: "Property", width: "30%" },
    { key: "value", label: "Value", width: "30%" },
    { key: "description", label: "Description", width: "40%" },
  ];

  const handleMetadataInference = async () => {
    if (!selectedData) return;

    setIsGeneratingMetadata(true);
    setError(null);

    try {
      // Call inference API with the job_id
      const inferenceResponse = await ApiService.runMetadataInference(
        selectedData?.value
      );

      // Check if we have metadata to display
      if (
        !inferenceResponse.full_metadata ||
        inferenceResponse.full_metadata.length === 0
      ) {
        setMetadataTableData([]);
        setMetadataColumns([]);
        setShowMetadataTable(false);
        setError("No metadata could be inferred from the selected data file.");
        return;
      }

      // Transform the inference results into table data
      const tableData: DAPTableRow[] = inferenceResponse.full_metadata.map(
        (field: any, index: number) => ({
          id: index + 1,
          ...field,
        })
      );

      // Update data_metadata_mapper table if metadata file was created
      if (inferenceResponse.metadata_file_id && selectedData) {
        try {
          // Create mapping between data file and metadata file
          const mappingResult = await ApiService.createDataMetadataMapping(
            selectedData.value,
            inferenceResponse.metadata_file_id,
            "system" // In a real app, this would come from user context
          );
          console.log("Data-metadata mapping created:", mappingResult);
        } catch (mappingError) {
          console.error("Failed to create data-metadata mapping:", mappingError);
          // Don't stop the process if mapping creation fails
        }
      }

      // Generate dynamic columns based on the first row of data
      if (tableData.length > 0) {
        const sampleRow = tableData[0];
        const columns: DAPTableColumn[] = Object.keys(sampleRow)
          .filter((key) => key !== "id") // Exclude the id field we added
          .map((key) => {
            const column: DAPTableColumn = {
              key,
              label: key
                .replace(/_/g, " ")
                .replace(/\b\w/g, (l) => l.toUpperCase()),
              width: "auto",
            };

            // Add special rendering for certain field types
            const sampleValue = sampleRow[key];
            if (typeof sampleValue === "boolean") {
              column.align = "center";
              column.render = (value: boolean) => (
                <span
                  className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                    value
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {value ? "True" : "False"}
                </span>
              );
            } else if (
              key.toLowerCase().includes("type") ||
              key.toLowerCase().includes("dtype")
            ) {
              column.render = (value: string) => (
                <code className="bg-secondary-100 px-2 py-1 rounded text-xs">
                  {value}
                </code>
              );
            } else if (Array.isArray(sampleValue)) {
              column.render = (value: any[]) => (
                <span className="text-xs">
                  {Array.isArray(value)
                    ? value.slice(0, 3).join(", ") +
                      (value.length > 3 ? "..." : "")
                    : String(value)}
                </span>
              );
            } else if (typeof sampleValue === "number") {
              column.align = "center";
            }

            return column;
          });

        setMetadataColumns(columns);
      }

      setMetadataTableData(tableData);
      setShowMetadataTable(true);
      setIsMetadataTableCollapsed(false);

      // Extract filename from metadata_file_saved path and set as selected metadata file
      if (inferenceResponse.metadata_file_saved) {
        const filePath = inferenceResponse.metadata_file_saved;
        const fileName = filePath.split(/[\\/]/).pop(); // Extract filename from path (handles both / and \)

        if (fileName) {
          // Find the metadata file option that matches this filename
          const matchingMetadataOption = metadataOptions.find(
            (option) =>
              option.label === fileName || option.label.includes(fileName)
          );

          if (matchingMetadataOption) {
            setSelectedMetadata(matchingMetadataOption);
            console.log("Auto-selected metadata file:", matchingMetadataOption);
          } else {
            // If not found in existing options, reload metadata files to get the new one
            console.log(
              "Metadata file not found in current options, reloading files..."
            );
            await loadFiles(); // This will refresh both data and metadata file lists

            // Try to find it again after reload
            setTimeout(() => {
              const updatedMetadataOption = metadataOptions.find(
                (option) =>
                  option.label === fileName || option.label.includes(fileName)
              );
              if (updatedMetadataOption) {
                setSelectedMetadata(updatedMetadataOption);
                console.log(
                  "Auto-selected metadata file after reload:",
                  updatedMetadataOption
                );
              }
            }, 500); // Small delay to ensure state is updated
          }
        }
      }

      console.log("Metadata inference completed:", inferenceResponse);
    } catch (err) {
      console.error("Error running metadata inference:", err);
      setError("Failed to run metadata inference. Please try again.");
      setMetadataTableData([]);
      setMetadataColumns([]);
      setShowMetadataTable(false);
    } finally {
      setIsGeneratingMetadata(false);
    }
  };
  const handleMetadataSelection = async (
    selectedOption: SelectOption | null
  ) => {
    setSelectedMetadata(selectedOption);

    // Clear all validation-related state when metadata file changes
    setIsMetadataValidationPassed(false);
    setMetaColumnData([]); // Clear validation results display
    setMetaDataFileId(undefined); // Clear metadata file ID

    // Clear structural relationships
    setRelationshipRows([{
      id: '1',
      sourceColumn: null,
      targetColumn: null,
      relationshipFactor: null
    }]);

    // Clear required description columns state
    setSelectedRoleFilter(null);
    setSelectedColumns([]);
    setAvailableColumnsForRole([]);

    if (selectedOption) {
      setIsGeneratingMetadata(true);
      setError(null);

      try {
        // Call the metadata inference API to view the metadata file content
        const metadataResponse = await ApiService.viewMetadataFile(
          selectedOption.value
        );

        // Check if we have preview data to display
        if (
          !metadataResponse.preview ||
          metadataResponse.preview.length === 0
        ) {
          setMetadataTableData([]);
          setMetadataColumns([]);
          setShowMetadataTable(false);
          setError("No data found in the selected metadata file.");
          return;
        }

        // Transform the metadata file preview into table data
        const tableData: DAPTableRow[] = metadataResponse.preview.map(
          (row: any, index: number) => ({
            id: index + 1,
            ...row,
          })
        );

        // Generate dynamic columns based on the first row of data
        if (tableData.length > 0) {
          const sampleRow = tableData[0];
          const columns: DAPTableColumn[] = Object.keys(sampleRow)
            .filter((key) => key !== "id") // Exclude the id field we added
            .map((key) => {
              const column: DAPTableColumn = {
                key,
                label: key
                  .replace(/_/g, " ")
                  .replace(/\b\w/g, (l) => l.toUpperCase()),
                width: "auto",
              };

              // Add special rendering for certain field types
              const sampleValue = sampleRow[key];
              if (typeof sampleValue === "boolean") {
                column.align = "center";
                column.render = (value: boolean) => (
                  <span
                    className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                      value
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {value ? "True" : "False"}
                  </span>
                );
              } else if (
                key.toLowerCase().includes("type") ||
                key.toLowerCase().includes("dtype")
              ) {
                column.render = (value: string) => (
                  <code className="bg-secondary-100 px-2 py-1 rounded text-xs">
                    {value}
                  </code>
                );
              } else if (Array.isArray(sampleValue)) {
                column.render = (value: any[]) => (
                  <span className="text-xs">
                    {Array.isArray(value)
                      ? value.slice(0, 3).join(", ") +
                        (value.length > 3 ? "..." : "")
                      : String(value)}
                  </span>
                );
              } else if (typeof sampleValue === "number") {
                column.align = "center";
              }

              return column;
            });

          setMetadataColumns(columns);
        }

        setMetadataTableData(tableData);
        setShowMetadataTable(true);
        setIsMetadataTableCollapsed(false); // Expand the table to show the data

        console.log("Loaded metadata file content:", {
          file_name: metadataResponse.file_name,
          row_count: metadataResponse.row_count,
          columns: metadataResponse.columns,
        });
      } catch (err) {
        console.error("Error loading metadata file:", err);
        setError("Failed to load metadata file content. Please try again.");
        setMetadataTableData([]);
        setMetadataColumns([]);
        setShowMetadataTable(false);
      } finally {
        setIsGeneratingMetadata(false);
      }
    } else {
      // Clear metadata table when no file is selected
      setMetadataTableData([]);
      setMetadataColumns([]);
      setShowMetadataTable(false);
    }
  };

  // Helper functions for Structural Relationship Definition
  const getColumnOptions = (): SelectOption[] => {
    if (!metadataTableData || metadataTableData.length === 0) return [];

    // Extract column names from metadata table data
    return metadataTableData.map((row, index) => ({
      value: index + 1, // Use numeric index as value
      label: row.column_name || `Column ${index + 1}`
    }));
  };

  const relationshipFactorOptions: SelectOption[] = [
    { value: 1, label: '1:1 (One-to-One)' },
    { value: 2, label: '1:M (One-to-Many)' },
    { value: 3, label: 'M:1 (Many-to-One)' },
    { value: 4, label: 'M:N (Many-to-Many)' }
  ];

  const addRelationshipRow = () => {
    const newId = (relationshipRows.length + 1).toString();
    setRelationshipRows([...relationshipRows, {
      id: newId,
      sourceColumn: null,
      targetColumn: null,
      relationshipFactor: null
    }]);
  };

  const removeRelationshipRow = (id: string) => {
    if (relationshipRows.length > 1) {
      setRelationshipRows(relationshipRows.filter(row => row.id !== id));
    }
  };

  const updateRelationshipRow = (id: string, field: 'sourceColumn' | 'targetColumn' | 'relationshipFactor', value: SelectOption | null) => {
    setRelationshipRows(relationshipRows.map(row =>
      row.id === id ? { ...row, [field]: value } : row
    ));
  };

  const getAvailableTargetColumns = (sourceColumnValue: number | null): SelectOption[] => {
    const allColumns = getColumnOptions();
    if (!sourceColumnValue) return allColumns;

    // Exclude the selected source column from target options
    return allColumns.filter(option => option.value !== sourceColumnValue);
  };

  const handleInferRelationships = async () => {
    try {
      // Collect selected source and target columns from relationship rows
      const selectedRelationships = relationshipRows
        .filter(row => row.sourceColumn && row.targetColumn)
        .map(row => {
          // Get the actual column names from metadata using the numeric indices
          // Note: getColumnOptions uses index + 1, so we need to subtract 1 for array access
          const sourceIndex = row.sourceColumn!.value - 1;
          const targetIndex = row.targetColumn!.value - 1;
          const sourceColumnName = metadataTableData[sourceIndex]?.column_name || '';
          const targetColumnName = metadataTableData[targetIndex]?.column_name || '';

          return {
            source_column: sourceColumnName,
            target_column: targetColumnName
          };
        });

      // Prepare the JSON payload with selected columns and metadata content
      const payload = {
        relationships: selectedRelationships,
        metadata_content: metadataTableData
      };

      console.log('Inferring relationships with payload:', payload);

      // Call the dummy infer relationships API
      const result = await ApiService.inferRelationships(payload);
      console.log('Inference result:', result);

      // Show success message
      setShowToast(true);
      setToastType("success");

    } catch (error) {
      console.error('Error inferring relationships:', error);
      setShowToast(true);
      setToastType("error");
    }
  };

  const loadExistingRelationships = async (metadataFileId: number) => {
    try {
      const existingRelationships = await ApiService.getStructuralRelationships(metadataFileId);
      if (existingRelationships && existingRelationships.relationships) {
        const columnOptions = getColumnOptions();
        const loadedRows = existingRelationships.relationships.map((rel: any, index: number) => {
          const sourceOption = columnOptions.find(opt => opt.label === rel.source_column);
          const targetOption = columnOptions.find(opt => opt.label === rel.target_column);
          const factorOption = relationshipFactorOptions.find(opt => opt.label === rel.relationship_factor);

          return {
            id: (index + 1).toString(),
            sourceColumn: sourceOption || null,
            targetColumn: targetOption || null,
            relationshipFactor: factorOption || null
          };
        });

        setRelationshipRows(loadedRows.length > 0 ? loadedRows : [{
          id: '1',
          sourceColumn: null,
          targetColumn: null,
          relationshipFactor: null
        }]);

        console.log("Loaded existing structural relationships:", loadedRows);
      }
    } catch (error) {
      console.error("Failed to load existing relationships:", error);
      // Don't show error to user as this is not critical
    }
  };

  // Helper functions for Required Description Columns
  const getRoleOptionsFromMetadata = (): SelectOption[] => {
    if (!metadataTableData || metadataTableData.length === 0) return [];

    // Extract unique role values from metadata table data
    // Assuming there's a 'role' field in the metadata, but if not, we'll use data_type or create default roles
    const roleField = 'role'; // This might need to be adjusted based on actual metadata structure
    const roles = new Set<string>();

    metadataTableData.forEach((row, index) => {
      const roleValue = row[roleField] || row.data_type || 'feature'; // Fallback to data_type or default
      if (roleValue && typeof roleValue === 'string') {
        roles.add(roleValue);
      }
    });

    // Convert to SelectOption format
    return Array.from(roles).map((role, index) => ({
      value: index + 1,
      label: role
    }));
  };

  const refreshAvailableColumns = () => {
    if (!metadataTableData || metadataTableData.length === 0 || !selectedRoleFilter) {
      setAvailableColumnsForRole([]);
      return;
    }

    // Filter columns based on selected role
    const roleField = 'role'; // This might need to be adjusted based on actual metadata structure
    const filteredColumns: SelectOption[] = [];

    metadataTableData.forEach((row, index) => {
      const roleValue = row[roleField] || row.data_type || 'feature'; // Fallback to data_type or default
      if (roleValue === selectedRoleFilter.label) {
        const columnOption = {
          value: index + 1,
          label: row.column_name || `Column ${index + 1}`
        };

        // Only include if not already selected
        const isAlreadySelected = selectedColumns.some(selected => selected.label === columnOption.label);
        if (!isAlreadySelected) {
          filteredColumns.push(columnOption);
        }
      }
    });

    setAvailableColumnsForRole(filteredColumns);
  };

  const handleColumnSelect = (selectedOption: SelectOption | null) => {
    if (!selectedOption) return;

    // Add to selected columns (simplified - just the column option)
    setSelectedColumns([...selectedColumns, selectedOption]);

    // Refresh available columns to remove the selected one
    refreshAvailableColumns();
  };

  const handleColumnRemove = (columnToRemove: SelectOption) => {
    setSelectedColumns(selectedColumns.filter(selected => selected.label !== columnToRemove.label));

    // Refresh available columns to add the removed one back
    refreshAvailableColumns();
  };

  const getColumnRoleFromMetadata = (columnName: string): string => {
    const column = metadataTableData.find(col => col.column_name === columnName);
    if (!column) return 'feature'; // fallback

    // Check for role field first, then fallback to data_type
    return column.role || column.data_type || 'feature';
  };

  const getColumnChipColor = (columnName: string): string => {
    // Get the role for this specific column from metadata
    const columnRole = getColumnRoleFromMetadata(columnName);

    // Different color schemes based on role
    const roleColors: Record<string, string> = {
      'Primary Key': 'bg-red-100 text-red-800 border-red-200',
      'Foreign Key': 'bg-blue-100 text-blue-800 border-blue-200',
      'Business Key': 'bg-purple-100 text-purple-800 border-purple-200',
      'Descriptive': 'bg-green-100 text-green-800 border-green-200',
      'Measure': 'bg-orange-100 text-orange-800 border-orange-200',
      'Calculated': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'feature': 'bg-gray-100 text-gray-800 border-gray-200',
      'integer': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'float': 'bg-pink-100 text-pink-800 border-pink-200',
      'text': 'bg-teal-100 text-teal-800 border-teal-200',
      'categorical': 'bg-cyan-100 text-cyan-800 border-cyan-200'
    };

    return roleColors[columnRole] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  // Effect to refresh available columns when role or selected columns change
  useEffect(() => {
    refreshAvailableColumns();
  }, [selectedRoleFilter, selectedColumns, metadataTableData]);

  const loadExistingRequiredDescriptions = async (metadataFileId: number) => {
    try {
      const existingRequiredDescriptions = await ApiService.getRequiredDescriptionColumns(metadataFileId);
      if (existingRequiredDescriptions && existingRequiredDescriptions.column_requirements) {
        const columnOptions = getColumnOptions();
        const requirements = existingRequiredDescriptions.column_requirements;

        const loadedRows = Object.entries(requirements).map(([columnName, config]: [string, any], index) => {
          const columnOption = columnOptions.find(opt => opt.label === columnName);

          return {
            id: (index + 1).toString(),
            column: columnOption || null,
            requiresDescription: config.requires_description || false
          };
        });

        setSelectedColumns(loadedRows.length > 0 ? loadedRows.map((row: any) => row.column).filter(Boolean) : []);

        console.log("Loaded existing required description columns:", loadedRows);
      }
    } catch (error) {
      console.error("Failed to load existing required descriptions:", error);
      // Don't show error to user as this is not critical
    }
  };

  // Combined save function (keeping for backward compatibility if needed)
  const handleSaveAllMetadata = async () => {
    setIsSaving(true);
    try {
      await handleSaveMetadataOnly();
      await handleSaveStructuralRelationships();
      await handleSaveRequiredDescriptions();

      alert("All metadata details saved successfully!");

      // Reset validation state after successful save
      setIsMetadataValidationPassed(false);
      setRelationshipRows([{
        id: '1',
        sourceColumn: null,
        targetColumn: null,
        relationshipFactor: null
      }]);
      setSelectedColumns([]);
      setSelectedRoleFilter(null);
      setAvailableColumnsForRole([]);

    } catch (error) {
      console.error("Error in combined save operation:", error);
    } finally {
      setIsSaving(false);
    }
  };

  // Metadata save functions
  const handleSaveMetadataOnly = async () => {
    if (!selectedMetadata || !metadataTableData.length) {
      alert("No metadata to save");
      return;
    }

    setIsSavingMetadata(true);
    try {
      const metaPayload = {
        file_id: selectedMetadata.value,
        metadata_content: metadataTableData
      };

      const res = await ApiService.editMetaDataTable(metaPayload);
      if (!res.success) {
        alert("Failed to save metadata: " + (res.message || "Unknown error"));
        return;
      }

      // Update data_metadata_mapper table
      if (selectedData && res.success) {
        try {
          // Create mapping between data file and metadata file
          const mappingResult = await ApiService.createDataMetadataMapping(
            selectedData.value,
            res.metadata_file_id,
            "system" // In a real app, this would come from user context
          );
          console.log("Data-metadata mapping created:", mappingResult);
        } catch (mappingError) {
          console.error("Failed to create data-metadata mapping:", mappingError);
          // Don't show error to user as this is not critical
        }
      } else {
        console.log("Skipping data-metadata mapping creation: No data file selected or metadata save failed");
      }

      alert("Metadata saved successfully!");
      console.log("Metadata saved successfully:", res);

    } catch (error) {
      console.error("Error saving metadata:", error);
      alert("Error saving metadata. Please try again.");
    } finally {
      setIsSavingMetadata(false);
    }
  };

  const handleSaveStructuralRelationships = async () => {
    if (!selectedMetadata) {
      alert("Please select a metadata file first");
      return;
    }

    const validRelationships = relationshipRows.filter(row =>
      row.sourceColumn && row.targetColumn && row.relationshipFactor
    );

    if (validRelationships.length === 0) {
      alert("No valid relationships to save. Please define at least one complete relationship.");
      return;
    }

    setIsSavingRelationships(true);
    try {
      const relationshipsData = validRelationships.map(row => ({
        source_column: row.sourceColumn?.label || '',
        target_column: row.targetColumn?.label || '',
        relationship_factor: row.relationshipFactor?.label || ''
      }));

      await ApiService.saveStructuralRelationships(
        selectedMetadata.value,
        relationshipsData,
        "system" // TODO: Replace with actual user
      );

      alert("Structural relationships saved successfully!");
      console.log("Structural relationships saved successfully");

    } catch (error) {
      console.error("Failed to save structural relationships:", error);
      alert("Failed to save structural relationships. Please try again.");
    } finally {
      setIsSavingRelationships(false);
    }
  };

  const handleSaveRequiredDescriptions = async () => {
    if (!selectedMetadata) {
      alert("Please select a metadata file first");
      return;
    }

    const validRequiredDescriptions = selectedColumns.filter(selected => selected);

    if (validRequiredDescriptions.length === 0) {
      alert("No columns selected for description requirements. Please select at least one column.");
      return;
    }

    setIsSavingDescriptions(true);
    try {
      const requirementsData = validRequiredDescriptions.reduce((acc: Record<string, any>, selected) => {
        if (selected?.label) {
          const columnRole = getColumnRoleFromMetadata(selected.label);
          acc[selected.label] = {
            role: columnRole,
            requires_description: true
          };
        }
        return acc;
      }, {} as Record<string, any>);

      await ApiService.saveRequiredDescriptionColumns(
        selectedMetadata.value,
        requirementsData
      );

      alert("Required description columns saved successfully!");
      console.log("Required description columns saved successfully");

    } catch (error) {
      console.error("Failed to save required description columns:", error);
      alert("Failed to save required description columns. Please try again.");
    } finally {
      setIsSavingDescriptions(false);
    }
  };

  // Navigation functions for metadata sections
  const navigateToNextSection = () => {
    if (currentMetadataSection === 'metadata-info') {
      setCurrentMetadataSection('structural-relationships');
    } else if (currentMetadataSection === 'structural-relationships') {
      setCurrentMetadataSection('required-descriptions');
    }
  };

  const navigateToPreviousSection = () => {
    if (currentMetadataSection === 'required-descriptions') {
      setCurrentMetadataSection('structural-relationships');
    } else if (currentMetadataSection === 'structural-relationships') {
      setCurrentMetadataSection('metadata-info');
    }
  };

  if (!isActive) return null;

  return (
    <div className="p-6 max-w-6xl ">
      <div className="w-full mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-2xl font-bold mb-1 text-gray-800">
                Metadata Management
              </h1>
              <p className="text-secondary-600">
                Manage and validate metadata files for data integrity and
                compliance
              </p>
            </div>
          </div>
        </div>
        {/* Main Content */}
        <div
          className={`grid grid-cols-1 lg:grid-cols-${
            isMetadataTableCollapsed === true ? 1 : 1
          } gap-6`}
        >
          {/* Center Content - File Selection */}
          <div className={`${isMetadataTableCollapsed === true ? 'lg:col-span-1' : 'lg:col-span-1'}`}>
            <div className="card file-selection-card" id="file-selection-container">
              <div className="mb-6">
                <h2 className="text-xl font-semibold text-secondary-900 flex items-center">
                  <DocumentIcon className="w-6 h-6 text-primary-600 mr-3" />
                  File Selection
                </h2>
              </div>

              {error && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
                  <ExclamationTriangleIcon className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
                  <div>
                    <p className="text-red-800 font-medium">Error</p>
                    <p className="text-red-700 text-sm">{error}</p>
                  </div>
                </div>
              )}

              <div className="space-y-6">
                {/* Data File Selection */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Data File
                  </label>
                  <div className="flex gap-3 items-center">
                    <div className="flex-1">
                      <Select
                        value={selectedData}
                        onChange={setSelectedData}
                        options={dataOptions}
                        placeholder="Select data file..."
                        isLoading={isLoading}
                        isDisabled={isLoading}
                        styles={customSelectStyles}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>

                    {/* OR separator */}
                    <div className="flex items-center text-secondary-400 text-sm font-medium">
                      OR
                    </div>

                    <label
                      htmlFor="file-upload"
                      className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center whitespace-nowrap cursor-pointer
                        ${
                          isUploadingFile
                            ? "bg-secondary-100 text-secondary-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg"
                        }`}
                    >
                      {isUploadingFile ? (
                        <>
                          <ClockIcon className="w-4 h-4 mr-1 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <FolderOpenIcon className="w-4 h-4 mr-1" />
                          Choose File
                        </>
                      )}
                    </label>
                    <input
                      type="file"
                      id="file-upload"
                      onChange={handleLocalFileSelection}
                      disabled={isUploadingFile}
                      accept=".csv,.xlsx,.xls,.json,.txt"
                      className="hidden"
                    />

                    <button
                      onClick={handleMetadataInference}
                      title={
                        preflightApiError
                          ? "Infer Metadata (Disabled: Data file analysis failed)"
                          : !!selectedMetadata
                          ? "Infer Metadata (Disabled: Metadata file already selected)"
                          : !selectedData
                          ? "Infer Metadata (Disabled: Select a data file first)"
                          : isGeneratingMetadata
                          ? "Generating metadata..."
                          : "Infer Metadata"
                      }
                      disabled={
                        !!selectedMetadata ||
                        !selectedData ||
                        isGeneratingMetadata ||
                        preflightApiError
                      }
                      className={`px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors flex items-center
                        ${
                          !!selectedMetadata ||
                          !selectedData ||
                          isGeneratingMetadata ||
                          preflightApiError
                            ? "bg-gray-400 cursor-not-allowed"
                            : ""
                        }`}
                    >
                      {isGeneratingMetadata ? (
                        <>
                          <SparklesIcon className="w-4 h-4 mr-1 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <SparklesIcon className="w-4 h-4 mr-1" />
                          Infer
                        </>
                      )}
                    </button>
                  </div>

                  {/* Data File Report */}
                  <div className="mt-4">
                    <div className="border border-secondary-200 rounded-lg p-4 bg-secondary-50">
                      <div className="flex justify-between items-center mb-3">
                        <h3 className="text-sm font-semibold text-secondary-900 flex items-center">
                          <DocumentTextIcon className="w-4 h-4 text-primary-600 mr-2" />
                          Data File Report
                        </h3>
                        <button
                          onClick={() =>
                            setIsDataFileReportCollapsed(
                              !isDataFileReportCollapsed
                            )
                          }
                          className="text-secondary-600 hover:text-secondary-800 transition-colors p-1 rounded hover:bg-secondary-100"
                          title={
                            isDataFileReportCollapsed ? "Expand" : "Collapse"
                          }
                        >
                          {isDataFileReportCollapsed ? (
                            <ChevronDownIcon className="w-4 h-4" />
                          ) : (
                            <ChevronUpIcon className="w-4 h-4" />
                          )}
                        </button>
                      </div>

                      {!isDataFileReportCollapsed && (
                        <div className="transition-all duration-300 ease-in-out  ">
                          {dataFileReportData.length > 0 ? (
                            <div className="space-y-3">
                              {/* File Overview Cards - More Compact */}
                              <div className="grid grid-cols-4 gap-2">
                                <div className="bg-white p-2 rounded border border-secondary-200">
                                  <div className="flex items-center mb-1">
                                    <span className="text-sm mr-1">📄</span>
                                    <span className="text-xs font-medium text-secondary-600">
                                      Status
                                    </span>
                                  </div>
                                  <p className="text-xs font-semibold text-secondary-900 truncate">
                                    {dataFileReportData.find(
                                      (item) =>
                                        item.property === "📄 File Status"
                                    )?.value || "Unknown"}
                                  </p>
                                </div>
                                <div className="bg-white p-2 rounded border border-secondary-200">
                                  <div className="flex items-center mb-1">
                                    <span className="text-sm mr-1">📏</span>
                                    <span className="text-xs font-medium text-secondary-600">
                                      Size
                                    </span>
                                  </div>
                                  <p className="text-xs font-semibold text-secondary-900 truncate">
                                    {dataFileReportData.find(
                                      (item) => item.property === "📏 File Size"
                                    )?.value || "Unknown"}
                                  </p>
                                </div>
                                <div className="bg-white p-2 rounded border border-secondary-200">
                                  <div className="flex items-center mb-1">
                                    <span className="text-sm mr-1">📋</span>
                                    <span className="text-xs font-medium text-secondary-600">
                                      Format
                                    </span>
                                  </div>
                                  <p className="text-xs font-semibold text-secondary-900 truncate">
                                    {dataFileReportData.find(
                                      (item) =>
                                        item.property === "📋 File Format"
                                    )?.value || "Unknown"}
                                  </p>
                                </div>
                                <div className="bg-white p-2 rounded border border-secondary-200">
                                  <div className="flex items-center mb-1">
                                    <span className="text-sm mr-1">🗂️</span>
                                    <span className="text-xs font-medium text-secondary-600">
                                      Columns
                                    </span>
                                    <button
                                      onClick={() => {
                                        setShowModal(true);
                                      }}
                                      className="ml-auto text-secondary-500 hover:text-primary-600"
                                      title="View Details"
                                    >
                                      <ArrowTopRightOnSquareIcon className="w-4 h-4" />
                                    </button>
                                  </div>
                                  <p className="text-xs font-semibold text-secondary-900 truncate">
                                    {dataFileReportData.find(
                                      (item) =>
                                        item.property === "🗂️ Number of Columns"
                                    )?.value || "Unknown"}
                                  </p>
                                </div>
                              </div>
                              {showModal && (
                                <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
                                  <div className="bg-white rounded-lg p-6 max-w-[1000px] w-full shadow-lg">
                                    {/* Column Analysis */}
                                    {dataFileReportData.find(
                                      (item) =>
                                        item.property === "🔍 Column Analysis"
                                    ) && (
                                      <div className="bg-white p-2 rounded ">
                                        <h4 className="text-xl font-semibold text-secondary-900 mb-6 flex items-center">
                                          Column Analysis (
                                          {
                                            dataFileReportData.filter((item) =>
                                              item.property.includes("└─")
                                            ).length
                                          }{" "}
                                          columns)
                                        </h4>
                                        <div className="grid grid-cols-1 gap-1 text-xs max-h-[500px] overflow-y-hidden">
                                          {/* {JSON.stringify(metaColumnData)} */}
                                          {metaColumnData && metaColumnData.length > 0 ? (
                                            <ColumnTable data={metaColumnData} />
                                          ) : (
                                            <p className="text-center py-4 text-secondary-500">No column data available</p>
                                          )}
                                        </div>
                                      </div>
                                    )}
                                    {/* <p className="text-sm text-secondary-800 break-words">
              {columnValue}
            </p> */}
                                    <div className="mt-6 text-right">
                                      <button
                                        onClick={() => setShowModal(false)}
                                        className="px-4 py-2 bg-primary-600 text-white text-sm rounded hover:bg-primary-700"
                                      >
                                        Close
                                      </button>
                                    </div>
                                  </div>
                                </div>
                              )}
                              {/* View & Edit Data Box and Technical Details in a flex row */}
                                <div className="grid grid-cols-2 gap-2">
                                    {/* View & Edit Data Box */}
                                    <div className="bg-white p-2 rounded border border-secondary-200  ">
                                        <DataBox
                                            dataFileId={selectedData ? selectedData.value : null}
                                            dataFileName={selectedData ? selectedData.label : undefined}
                                        />
                                    </div>

                                    {/* Technical Details */}
                                    <div className="flex-1 bg-blue-50 p-2 rounded border border-blue-200">
                                        <h4 className="text-xs font-semibold text-blue-800 mb-1 flex items-center">
                                            <span className="mr-1">⚙️</span>
                                            Technical Details
                                        </h4>
                                        <div className="grid grid-cols-2 gap-1 text-xs">
                                            <div>
                                                <span className="text-blue-600">
                                                    Encoding:
                                                </span>
                                                <span className="ml-1 text-blue-900">
                                                    {dataFileReportData.find(
                                                        (item) =>
                                                            item.property === "🔤 Encoding"
                                                    )?.value || "Unknown"}
                                                </span>
                                            </div>
                                            <div>
                                                <span className="text-blue-600">
                                                    Memory:
                                                </span>
                                                <span className="ml-1 text-blue-900">
                                                    {dataFileReportData.find(
                                                        (item) =>
                                                            item.property === "💾 Memory Usage"
                                                    )?.value || "Unknown"}
                                                </span>
                                            </div>
                                            <div className="col-span-2">
                                                <span className="text-blue-600">
                                                    CSV Format:
                                                </span>
                                                <span className="ml-1 text-blue-900">
                                                    {dataFileReportData.find(
                                                        (item) =>
                                                            item.property === "📊 CSV Format"
                                                    )?.value || "Unknown"}
                                                </span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                              {/* Recommendation */}
                              {dataFileReportData.find(
                                (item) => item.property === "💡 Recommendation"
                              ) && (
                                <div className="bg-yellow-50 p-2 rounded border border-yellow-200">
                                  <h4 className="text-xs font-semibold text-yellow-800 mb-1 flex items-center">
                                    <span className="mr-1">💡</span>
                                    Recommendation
                                  </h4>
                                  <p className="text-xs text-yellow-900">
                                    {
                                      dataFileReportData.find(
                                        (item) =>
                                          item.property === "💡 Recommendation"
                                      )?.value
                                    }
                                  </p>
                                </div>
                              )}
                            </div>
                          ) : (
                            <div className="text-center py-6 text-secondary-500">
                              <DocumentTextIcon className="w-10 h-10 mx-auto mb-3 text-secondary-300" />
                              <p className="text-sm">No Content</p>
                              <p className="text-xs mt-1">
                                Select a data file to generate report
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Divider between Data File and Metadata File sections */}
                <div className="border-t border-secondary-200 my-6"></div>

                {/* Divider between Data File and Metadata File sections */}
                <div className="border-t border-secondary-200 my-6"></div>

                {/* Metadata File Selection */}
                <div>
                  <label className="block text-sm font-medium text-secondary-700 mb-2">
                    Metadata File
                  </label>
                  <div className="flex gap-3 items-center">
                    <div className="flex-1">
                      <Select
                        value={selectedMetadata}
                        onChange={handleMetadataSelection}
                        options={metadataOptions}
                        placeholder="Select metadata file..."
                        isLoading={isLoading}
                        isDisabled={isLoading || preflightApiError || !selectedData}
                        styles={customSelectStyles}
                        className="react-select-container"
                        classNamePrefix="react-select"
                      />
                    </div>

                    {/* OR separator */}
                    <div className="flex items-center text-secondary-400 text-sm font-medium">
                      OR
                    </div>

                    <label
                      htmlFor="metadata-file-upload"
                      className={`px-4 py-2 rounded-lg font-medium transition-all duration-200 flex items-center justify-center whitespace-nowrap cursor-pointer
                        ${
                          isUploadingMetadataFile || preflightApiError || !selectedData
                            ? "bg-secondary-100 text-secondary-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg"
                        }`}
                    >
                      {isUploadingMetadataFile ? (
                        <>
                          <ClockIcon className="w-4 h-4 mr-1 animate-spin" />
                          Uploading...
                        </>
                      ) : (
                        <>
                          <FolderOpenIcon className="w-4 h-4 mr-1" />
                          Choose File
                        </>
                      )}
                    </label>
                    <input
                      type="file"
                      id="metadata-file-upload"
                      onChange={handleLocalMetadataFileSelection}
                      disabled={isUploadingMetadataFile || preflightApiError || !selectedData}
                      accept=".csv"
                      className="hidden"
                    />
                  </div>

                  {/* Warning message when disabled due to preflight API error or no data file */}
                  {(preflightApiError || !selectedData) && (
                    <div className="mt-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg flex items-start">
                      <ExclamationTriangleIcon className="w-4 h-4 text-yellow-500 mt-0.5 mr-2 flex-shrink-0" />
                      <div>
                        <p className="text-yellow-800 text-sm font-medium">
                          Metadata selection disabled
                        </p>
                        <p className="text-yellow-700 text-xs mt-1">
                          {!selectedData
                            ? "Please select a data file first to enable metadata selection."
                            : "Data file analysis failed. Please select a valid data file first."}
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Metadata Info Table */}
                  {currentMetadataSection === 'metadata-info' && (
                  <div className="mt-4">
                    <div className="border border-secondary-200 rounded-lg p-4 bg-secondary-50">
                      <div className="flex justify-between items-center mb-3">
                        <h2 className="text-xl font-semibold text-secondary-900 flex items-center">
                            <DocumentIcon className="w-6 h-6 text-primary-600 mr-3" />
                          Metadata Info Table
                        </h2>
                        <button
                          onClick={() =>
                            setIsMetadataTableCollapsed(
                              !isMetadataTableCollapsed
                            )
                          }
                          className="text-secondary-600 hover:text-secondary-800 transition-colors p-1 rounded hover:bg-secondary-100"
                          title={
                            isMetadataTableCollapsed ? "Expand" : "Collapse"
                          }
                        >
                          {isMetadataTableCollapsed ? (
                            <ChevronDownIcon className="w-4 h-4" />
                          ) : (
                            <ChevronUpIcon className="w-4 h-4" />
                          )}
                        </button>
                      </div>

                      {!isMetadataTableCollapsed && (
                        <div
                          className="transition-all duration-300 ease-in-out"
                          style={{ position: "relative" }}
                        >
                          {metadataTableData.length > 0 ? (
                            <DAPTable
                              key={selectedMetadata?.value || 'no-metadata'} // Force re-render when metadata file changes
                              columns={metadataColumns}
                              showSaveButton={false}
                              data={metadataTableData}
                              showTitle={true}
                              fileId={metaDataFileId}
                              setShowToast={(data: boolean) => {
                                setShowToast(data);
                                const scrollable =
                                  document.getElementById("file-selection-container");
                                scrollable?.scrollTo({
                                  top: 0,
                                  behavior: "smooth",
                                });
                              }}
                              setToastType={(data) => {
                                setToastType(data);
                              }}
                              onMetadataSaved={handleMetadataSaved}
                              onValidationStatusChange={setIsMetadataValidationPassed}
                              emptyMessage="No metadata generated yet. Select metadata file or use Infer button to generate metadata."
                            />
                          ) : (
                            <div className="text-center py-6 text-secondary-500">
                              <DocumentIcon className="w-10 h-10 mx-auto mb-3 text-secondary-300" />
                              <p className="text-sm">No Content</p>
                              <p className="text-xs mt-1">
                                No metadata generated yet. Select metadata file
                                or use Infer button to generate metadata
                              </p>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  )}
                  {/* Save Metadata Button - Only shown after validation passes */}
                  {selectedMetadata && metadataTableData.length > 0 && isMetadataValidationPassed && currentMetadataSection === 'metadata-info' && (
                    <div className="mt-4">
                      <div className="flex justify-between">
                        <button
                          onClick={handleSaveMetadataOnly}
                          disabled={isSavingMetadata || !selectedMetadata || !metadataTableData.length}
                          className={`px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 ${
                            isSavingMetadata || !selectedMetadata || !metadataTableData.length
                              ? "bg-gray-400 cursor-not-allowed"
                              : "bg-blue-600 hover:bg-blue-700 shadow-md hover:shadow-lg"
                          }`}
                          title="Save metadata table content"
                        >
                          {isSavingMetadata ? "Saving..." : "Save Metadata"}
                        </button>

                        <button
                          onClick={navigateToNextSection}
                          className="px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 bg-purple-600 hover:bg-purple-700 shadow-md hover:shadow-lg"
                          title="Go to Structural Relationship Definition"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                  {/* Structural Relationship Definition Section */}
                  {selectedMetadata && metadataTableData.length > 0 && isMetadataValidationPassed && currentMetadataSection === 'structural-relationships' && (
                    <>
                      <div className="mt-6">
                        <label className="block text-sm font-medium text-secondary-700 mb-4">
                        <h2 className="text-xl font-semibold text-secondary-900 flex items-center">
                            <DocumentIcon className="w-6 h-6 text-primary-600 mr-3" />
                          Structural Relationship Definition
                          </h2>
                        </label>
                        <div className="border border-secondary-200 rounded-lg p-4 bg-secondary-50">
                          <div className="flex justify-between items-center mb-4">
                            <h3 className="text-sm font-semibold text-secondary-900 flex items-center">
                              <DocumentIcon className="w-4 h-4 text-primary-600 mr-2" />
                              Column Relationships
                            </h3>
                            <button
                              onClick={addRelationshipRow}
                              className="px-3 py-1 bg-primary-600 text-white text-xs rounded hover:bg-primary-700 transition-colors"
                            >
                              + Add Row
                            </button>
                          </div>

                          {/* Relationship Rows */}
                          <div className="space-y-3">
                            {relationshipRows.map((row) => (
                              <div key={row.id} className="grid grid-cols-12 gap-3 items-center bg-white p-3 rounded border">
                                {/* Source Column */}
                                <div className="col-span-4">
                                  <label className="block text-xs font-medium text-secondary-600 mb-1">
                                    Source Column
                                  </label>
                                  <Select
                                    value={row.sourceColumn}
                                    onChange={(option) => updateRelationshipRow(row.id, 'sourceColumn', option)}
                                    options={getColumnOptions()}
                                    placeholder="Select source..."
                                    styles={customSelectStyles}
                                    className="react-select-container text-sm"
                                    classNamePrefix="react-select"
                                  />
                                </div>

                                {/* Target Column */}
                                <div className="col-span-4">
                                  <label className="block text-xs font-medium text-secondary-600 mb-1">
                                    Target Column
                                  </label>
                                  <Select
                                    value={row.targetColumn}
                                    onChange={(option) => updateRelationshipRow(row.id, 'targetColumn', option)}
                                    options={getAvailableTargetColumns(row.sourceColumn?.value || null)}
                                    placeholder="Select target..."
                                    styles={customSelectStyles}
                                    className="react-select-container text-sm"
                                    classNamePrefix="react-select"
                                    isDisabled={!row.sourceColumn}
                                  />
                                </div>

                                {/* Relationship Factor */}
                                <div className="col-span-3">
                                  <label className="block text-xs font-medium text-secondary-600 mb-1">
                                    Relationship Factor
                                  </label>
                                  <Select
                                    value={row.relationshipFactor}
                                    onChange={(option) => updateRelationshipRow(row.id, 'relationshipFactor', option)}
                                    options={relationshipFactorOptions}
                                    placeholder="Select factor..."
                                    styles={customSelectStyles}
                                    className="react-select-container text-sm"
                                    classNamePrefix="react-select"
                                  />
                                </div>

                                {/* Remove Button */}
                                <div className="col-span-1 flex justify-center">
                                  <button
                                    onClick={() => removeRelationshipRow(row.id)}
                                    disabled={relationshipRows.length === 1}
                                    className={`p-1 rounded transition-colors ${
                                      relationshipRows.length === 1
                                        ? 'text-secondary-300 cursor-not-allowed'
                                        : 'text-red-500 hover:text-red-700 hover:bg-red-50'
                                    }`}
                                    title="Remove row"
                                  >
                                    <XCircleIcon className="w-4 h-4" />
                                  </button>
                                </div>
                              </div>
                            ))}
                          </div>

                          {/* Infer Relationship Button */}
                          <div className="mt-4 flex justify-center">
                            <button
                              onClick={handleInferRelationships}
                              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                            >
                              <SparklesIcon className="w-4 h-4 mr-2" />
                              Infer Relationship
                            </button>
                          </div>

                          {/* Help Text */}
                          <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                            <p className="font-medium mb-1">Relationship Factors:</p>
                            <ul className="space-y-1 text-blue-600">
                              <li><strong>1:1</strong> - Each source record relates to exactly one target record</li>
                              <li><strong>1:M</strong> - Each source record can relate to multiple target records</li>
                              <li><strong>M:1</strong> - Multiple source records can relate to one target record</li>
                              <li><strong>M:N</strong> - Multiple source records can relate to multiple target records</li>
                            </ul>
                          </div>

                          {/* Navigation and Save Buttons */}
                          <div className="mt-4 pt-3 border-t border-secondary-200 flex justify-between">
                            <button
                              onClick={navigateToPreviousSection}
                              className="px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 bg-gray-600 hover:bg-gray-700 shadow-md hover:shadow-lg"
                              title="Go back to Metadata Info Table"
                            >
                              Back
                            </button>

                            <button
                              onClick={handleSaveStructuralRelationships}
                              disabled={isSavingRelationships || relationshipRows.filter(row => row.sourceColumn && row.targetColumn && row.relationshipFactor).length === 0}
                              className={`px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 ${
                                isSavingRelationships || relationshipRows.filter(row => row.sourceColumn && row.targetColumn && row.relationshipFactor).length === 0
                                  ? "bg-gray-400 cursor-not-allowed"
                                  : "bg-purple-600 hover:bg-purple-700 shadow-md hover:shadow-lg"
                              }`}
                              title="Save structural relationship definitions"
                            >
                              {isSavingRelationships ? "Saving..." : "Save Relationship Details"}
                            </button>

                            <button
                              onClick={navigateToNextSection}
                              className="px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 bg-purple-600 hover:bg-purple-700 shadow-md hover:shadow-lg"
                              title="Go to Required Description Columns"
                            >
                              Next
                            </button>
                          </div>
                        </div>
                      </div>
                    </>
                  )}
                  {selectedMetadata && metadataTableData.length > 0 && isMetadataValidationPassed && currentMetadataSection === 'required-descriptions' && (
                    <div className="mt-6">
                      <label className="block text-sm font-medium text-secondary-700 mb-4">
                      <h2 className="text-xl font-semibold text-secondary-900 flex items-center">
                        <DocumentIcon className="w-6 h-6 text-primary-600 mr-3" />
                        Required Description Columns
                        </h2>
                      </label>
                      <div className="border border-secondary-200 rounded-lg p-4 bg-secondary-50">
                        {/* Role Filter Section */}
                        <div className="mb-6">
                          <h3 className="text-sm font-semibold text-secondary-900 flex items-center mb-3">
                            <DocumentIcon className="w-4 h-4 text-primary-600 mr-2" />
                            Filter Columns by Role
                          </h3>
                          <div className="grid grid-cols-12 gap-3 items-center">
                            <div className="col-span-6">
                              <label className="block text-xs font-medium text-secondary-600 mb-1">
                                Select Role to Filter Columns
                              </label>
                              <Select
                                value={selectedRoleFilter}
                                onChange={setSelectedRoleFilter}
                                options={getRoleOptionsFromMetadata()}
                                placeholder="Select a role to filter columns..."
                                styles={customSelectStyles}
                                className="react-select-container text-sm"
                                classNamePrefix="react-select"
                              />
                            </div>
                            <div className="col-span-6">
                              <div className="text-xs text-secondary-600 mt-6">
                                {selectedRoleFilter
                                  ? `Showing columns with role: ${selectedRoleFilter.label}`
                                  : 'Select a role to see available columns'
                                }
                              </div>
                            </div>
                          </div>
                        </div>

                        {/* Selected Columns as Chips - Always visible when columns exist */}
                        {selectedColumns.length > 0 && (
                          <div className="mb-4">
                            <h4 className="text-sm font-semibold text-secondary-900 mb-3">
                              Selected Columns ({selectedColumns.length})
                            </h4>
                            <div className="bg-white p-4 rounded border border-secondary-200">
                              <div className="flex flex-wrap gap-2">
                                {selectedColumns.map((selected, index) => (
                                  <div
                                    key={index}
                                    className={`inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium border ${getColumnChipColor(selected.label)} transition-all duration-200`}
                                  >
                                    <span className="mr-2">{selected.label}</span>

                                    {/* Remove Button */}
                                    <button
                                      onClick={() => handleColumnRemove(selected)}
                                      className="ml-1 text-red-500 hover:text-red-700 transition-colors"
                                      title="Remove column"
                                    >
                                      <XCircleIcon className="w-4 h-4" />
                                    </button>
                                  </div>
                                ))}
                              </div>

                              {/* Legend */}
                              <div className="mt-3 pt-3 border-t border-secondary-200">
                                <div className="flex items-center text-xs text-secondary-600">
                                  <span className="mr-4">
                                    <strong>Colors:</strong> Role-based color coding
                                  </span>
                                  <span>
                                    <strong>✕:</strong> Remove column
                                  </span>
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Column Selection Section - Only when role is selected */}
                        {selectedRoleFilter && (
                          <div className="mb-4">
                            <h3 className="text-sm font-semibold text-secondary-900 mb-3">
                              Add Columns for Description Requirements
                            </h3>
                            <div className="grid grid-cols-12 gap-3 items-end">
                              <div className="col-span-10">
                                <label className="block text-xs font-medium text-secondary-600 mb-1">
                                  Select Column (Role: {selectedRoleFilter.label})
                                </label>
                                <Select
                                  value={null}
                                  onChange={handleColumnSelect}
                                  options={availableColumnsForRole}
                                  placeholder={availableColumnsForRole.length > 0 ? "Select a column to add..." : "No more columns available"}
                                  styles={customSelectStyles}
                                  className="react-select-container text-sm"
                                  classNamePrefix="react-select"
                                  isDisabled={availableColumnsForRole.length === 0}
                                />
                              </div>
                              <div className="col-span-2">
                                <div className="text-xs text-secondary-500">
                                  {availableColumnsForRole.length} available
                                </div>
                              </div>
                            </div>
                          </div>
                        )}

                        {/* Help Text */}
                        <div className="mt-3 p-2 bg-blue-50 border border-blue-200 rounded text-xs text-blue-700">
                          <p className="font-medium mb-1">Role-Based Column Filtering:</p>
                          <p className="text-blue-600">
                            Select a role from the metadata to filter and display only columns with that specific role.
                            This helps you focus on columns that serve similar purposes in your data structure.
                          </p>
                        </div>

                        {/* Navigation and Save Button */}
                        <div className="mt-4 pt-3 border-t border-secondary-200 flex justify-between">
                          <button
                            onClick={navigateToPreviousSection}
                            className="px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 bg-gray-600 hover:bg-gray-700 shadow-md hover:shadow-lg"
                            title="Go back to Structural Relationship Definition"
                          >
                            Back
                          </button>

                          <button
                            onClick={handleSaveRequiredDescriptions}
                            disabled={isSavingDescriptions || selectedColumns.length === 0}
                            className={`px-4 py-2 text-white text-sm rounded-lg font-medium transition-all duration-200 ${
                              isSavingDescriptions || selectedColumns.length === 0
                                ? "bg-gray-400 cursor-not-allowed"
                                : "bg-green-600 hover:bg-green-700 shadow-md hover:shadow-lg"
                            }`}
                            title="Save required description column settings"
                          >
                            {isSavingDescriptions ? "Saving..." : "Save Description Requirements"}
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

        </div>
        <div className={'py-2 w-full flex justify-end'}>
         <button
              disabled={!selectedMetadata}
              className={`px-4 py-2 rounded-md shadow-sm transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 flex items-center ${
                selectedMetadata
                  ? "bg-blue-500 hover:bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
              title={selectedMetadata ? "Preview Profiling Rule - Generate JSON schema from metadata" : "Select a metadata file to preview profiling rule"}
              onClick={handlePreviewJson}
            >
              <CodeBracketIcon className="w-5 h-5 mr-2" />
              <span className="font-medium">Preview JSON</span>
            </button>
            </div>
      </div>
      {showToast && (
        <div className="fixed top-5 right-5 space-y-2 z-50">
          <ToastMessage
            message={
              toastType === "success"
                ? "Editted successfully"
                : "Unable to save the changes"
            }
            type={toastType}
            onClose={() => {
              setShowToast(false);
            }}
          />
        </div>
      )}



      {/* JSON Preview Overlay */}
      <JsonPreviewOverlay
        isOpen={showJsonOverlay}
        onClose={() => setShowJsonOverlay(false)}
        jsonData={jsonData}
        title="Profiling Rules JSON"
        loading={jsonLoading}
        error={jsonError || undefined}
      />
    </div>
  );
};

export default JobSection;
